import { useCallback } from 'react';
import { FormikHelpers, FormikProps } from 'formik';
import { TokenPayload } from '@recurly/recurly-js';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { useTranslation } from '@blocs.i18n';
import { FEATURE_FLAGS, useFeatureExperiment } from '../../FeaturesAndExperiments';
import { applySuggestedAddressToFormik, useAddressValidator } from '../AddressValidation';
import { isPaymentTypeApplePay } from '../CreditCard/CreditCard';
import { PAYMENT_TYPES } from '../enums/PaymentMethods';
import { usePaywallFunnelEvents } from '../usePaywallFunnelEvents';
import { getNewPaymentMethodPriority, hasBlockingValidationErrors } from '../usePaymentMethodForm';
import { hasCompleteBillingAddress } from './BillingAddressCard';
import { AddPaymentProps, PaymentFormSubmitPayload, PaymentMethodFormType } from './types';

type UseCheckoutAddPaymentParams = Pick<
  AddPaymentProps,
  | 'showAddPaymentMethod'
  | 'setShowAddPaymentMethod'
  | 'threeDActionContext'
  | 'wallet'
  | 'selectedPaymentOption'
  | 'onSelectPaymentOption'
  | 'onUpdatePaymentOption'
  | 'onAddPaymentOption'
  | 'paymentMethodFormikRef'
  | 'requestRecurlyTokenAndValidate'
  | 'onCompletePurchase'
  | 'onNext'
  | 'isModal'
  | 'paywallLocation'
>;

// Owns all of the checkout/upgrade flow's AddPayment logic on its own — also what edit
// membership/billing info pages exercise, since they render the same PaymentForm mode="checkout"
// flow with a saved card already selected. Deliberately not sharing a hook with the registration
// flow, even though a few calculations look similar, so that reading this file top to bottom tells
// you the whole upgrade story without following a reference into something also serving
// registration.
export const useCheckoutAddPayment = ({
  showAddPaymentMethod,
  setShowAddPaymentMethod,
  threeDActionContext,
  wallet,
  selectedPaymentOption,
  onSelectPaymentOption,
  onUpdatePaymentOption,
  onAddPaymentOption,
  paymentMethodFormikRef,
  requestRecurlyTokenAndValidate,
  onCompletePurchase,
  onNext,
  isModal,
  paywallLocation,
}: UseCheckoutAddPaymentParams) => {
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();
  const isPaywallBillingAddressEnabled = useFeatureExperiment(FEATURE_FLAGS.WEB_PAYWALL_BILLING_ADDRESS);
  const isWalletVisible = !showAddPaymentMethod && !threeDActionContext?.token && !!wallet?.length;

  const isIneligiblePaymentTypeForBillingAddress =
    isPaymentTypeApplePay(selectedPaymentOption?.paymentType) ||
    selectedPaymentOption?.paymentType === PAYMENT_TYPES.PAYPAL;
  const isSelectedCardEditableForBillingAddress = !!selectedPaymentOption && !isIneligiblePaymentTypeForBillingAddress;

  const isBillingAddressEditable =
    isWalletVisible &&
    isPaywallBillingAddressEnabled &&
    !!onUpdatePaymentOption &&
    isSelectedCardEditableForBillingAddress;

  const requiresBillingAddressBeforePurchase =
    isBillingAddressEditable && !hasCompleteBillingAddress(selectedPaymentOption);

  const funnelEvents = usePaywallFunnelEvents(paywallLocation, isModal);

  const { checkAddress, pendingAddressIssue, resolveAddressIssue, dismissAddressIssue } = useAddressValidator({
    onAddressCorrected: (address) => applySuggestedAddressToFormik(paymentMethodFormikRef.current, address),
  });

  const walletPickerProps = {
    isWalletVisible,
    isBillingAddressEditable,
    wallet,
    selectedPaymentOption,
    onSelectPaymentOption,
    setShowAddPaymentMethod,
    onUpdatePaymentOption,
    funnelEvents,
  };

  // The upgrade flow (the original V1 + PaymentSummary combo) sends the raw trial-terms-checkbox
  // state instead — false for non-trial upgrades — which PaymentForm merges into formData for us.
  // Unlike registration, there's no free-plan/cardless-trial short-circuit here: the free plan is
  // filtered out of every upgrade plan list, and cardless trials only ever originate from the
  // registration billing path, so neither is reachable once a card/billing method is being edited.
  // Also doesn't fire trackRegistrationCompleteEvent — both of its branches (open-call registration,
  // cardless-signup) are registration-only concepts, unreachable here; the flag one of them reads
  // (redirectToOpenCall) is also never cleared, so firing it here would risk misattributing a stale
  // open-call registration to this unrelated upgrade.
  const handleUpgradeSubmit = useCallback(
    async (formData: PaymentFormSubmitPayload, formikHelpers: FormikHelpers<PaymentMethodFormType>) => {
      funnelEvents.trackCheckoutSubmitted();
      const termsAccepted = formData.acceptedTrialTerms;
      if (await hasBlockingValidationErrors(formikHelpers)) return;
      if (!selectedPaymentOption) {
        const accepted = await checkAddress(formData);
        if (!accepted) return;
        requestRecurlyTokenAndValidate(formData, formikHelpers, async (token) => {
          await onCompletePurchase({ token: token.id, termsAccepted });
        });
      } else {
        await onCompletePurchase({ termsAccepted });
      }
    },
    [funnelEvents, onCompletePurchase, selectedPaymentOption, checkAddress, requestRecurlyTokenAndValidate]
  );

  const handleAddCardDuringCheckout = useCallback(
    async (formData: PaymentMethodFormType, formikHelpers: FormikProps<PaymentMethodFormType>) => {
      if (await hasBlockingValidationErrors(formikHelpers)) return;
      const accepted = await checkAddress(formData);
      if (!accepted) return;
      requestRecurlyTokenAndValidate(formData, formikHelpers, async (token) => {
        const { isBackupPaymentMethod, isPrimaryPaymentMethod } = getNewPaymentMethodPriority(
          paymentMethodFormikRef.current?.values,
          !wallet?.length
        );
        await onAddPaymentOption({ token, isPrimaryPaymentMethod, isBackupPaymentMethod });
      });
    },
    [onAddPaymentOption, checkAddress, requestRecurlyTokenAndValidate, paymentMethodFormikRef, wallet]
  );

  const handleCheckoutPayPalToken = useCallback(
    async (token: TokenPayload) => {
      funnelEvents.trackCheckoutSubmitted();
      await onCompletePurchase({ token: token.id, termsAccepted: true });
      showMessage({ message: t('common:paywall.payment.success'), color: 'success' });
      onNext?.();
    },
    [funnelEvents, onCompletePurchase, showMessage, t, onNext]
  );

  return {
    walletPickerProps,
    requiresBillingAddressBeforePurchase,
    handleUpgradeSubmit,
    handleAddCardDuringCheckout,
    handleCheckoutPayPalToken,
    pendingAddressIssue,
    resolveAddressIssue,
    dismissAddressIssue,
  };
};
